export const setNestedValue = (
  obj: Record<string, any>,
  path: string,
  value: any,
): Record<string, any> => {
  const keys = path.split('.')
  const result = { ...obj }

  let current: Record<string, any> = result
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    current[key] =
      current[key] != null && typeof current[key] === 'object' ? { ...current[key] } : {}
    current = current[key]
  }

  current[keys[keys.length - 1]] = value
  return result
}

export const getNestedValue = (obj: Record<string, any>, path: string): any => {
  const keys = path.split('.')
  let current: any = obj
  for (const key of keys) {
    if (current == null || typeof current !== 'object') return undefined
    current = current[key]
  }
  return current
}

export const flattenValues = (obj: Record<string, any>, prefix = ''): Record<string, string> => {
  const result: Record<string, string> = {}

  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key

    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(result, flattenValues(value, path))
    } else {
      result[path] = value == null ? '' : String(value)
    }
  }

  return result
}

type FormFieldElement =
  | HTMLInputElement
  | HTMLTextAreaElement
  | HTMLSelectElement
  | HTMLImageElement

const getFieldKey = (element: Element): string | null =>
  element.getAttribute('name') || element.getAttribute('id')

const escapeAttributeValue = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')

export const findElementByFieldKey = (root: ParentNode, fieldKey: string): Element | null => {
  const escapedFieldKey = escapeAttributeValue(fieldKey)
  return (
    root.querySelector(`[name="${escapedFieldKey}"]`) ||
    root.querySelector(`[id="${escapedFieldKey}"]`)
  )
}

export const extractFormFields = (html: string): Record<string, any> => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  let values: Record<string, any> = {}

  const elements = doc.querySelectorAll(
    'input[name], input[id], textarea[name], textarea[id], select[name], select[id], img[name], img[id]',
  )

  elements.forEach((el) => {
    const element = el as FormFieldElement
    const fieldKey = getFieldKey(element)
    if (!fieldKey) return

    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') {
        values = setNestedValue(values, fieldKey, element.checked ? 'on' : '')
      } else {
        values = setNestedValue(values, fieldKey, element.value || '')
      }
      return
    }

    if (element instanceof HTMLImageElement) {
      values = setNestedValue(values, fieldKey, element.src || '')
      return
    }

    if (element instanceof HTMLTextAreaElement) {
      values = setNestedValue(values, fieldKey, element.value || '')
      return
    }

    if (element instanceof HTMLSelectElement) {
      values = setNestedValue(values, fieldKey, element.value || '')
    }
  })

  return values
}

// ---------------------------------------------------------------------------
// Required-field detection (signatures / witness)
// ---------------------------------------------------------------------------

export type RequiredFieldNames = {
  patientSignatureFieldName: string | null
  physicianSignatureFieldName: string | null
  witnessNameFieldName: string | null
  witnessSignatureFieldName: string | null
}

type FieldDescriptor = {
  key: string | null
  name: string | null
  id: string | null
  type: string | null
  tagName: string
  normalized: string
}

const normalize = (value: string | null | undefined) =>
  (value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim()

const getFieldDescriptors = (doc: Document): FieldDescriptor[] => {
  const elements = Array.from(doc.querySelectorAll('input, textarea, select, img'))
  return elements.map((element) => {
    const fieldName = element.getAttribute('name')
    const fieldId = element.getAttribute('id')
    const fieldKey = getFieldKey(element)
    const fieldType = element instanceof HTMLInputElement ? element.type : null
    const normalized = normalize(`${fieldName || ''} ${fieldId || ''}`)

    return {
      key: fieldKey,
      name: fieldName,
      id: fieldId,
      type: fieldType,
      tagName: element.tagName.toLowerCase(),
      normalized,
    }
  })
}

const findFieldKey = (
  fields: FieldDescriptor[],
  includePatterns: RegExp[],
  options?: {
    excludePatterns?: RegExp[]
    allowedTags?: string[]
    excludedTypes?: string[]
  },
): string | null => {
  const excludePatterns = options?.excludePatterns || []
  const allowedTags = options?.allowedTags
  const excludedTypes = options?.excludedTypes || []

  const match = fields.find((field) => {
    if (!field.key) return false
    if (allowedTags && !allowedTags.includes(field.tagName)) return false
    if (field.type && excludedTypes.includes(field.type.toLowerCase())) return false
    if (excludePatterns.some((pattern) => pattern.test(field.normalized))) return false

    return includePatterns.every((pattern) => pattern.test(field.normalized))
  })

  return match?.key || null
}

export const getRequiredFieldNames = (html: string): RequiredFieldNames => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const fields = getFieldDescriptors(doc)

  const patientSignatureFieldName =
    findFieldKey(fields, [/patient/, /signature/], {
      allowedTags: ['img', 'input'],
    }) ||
    findFieldKey(fields, [/patientsignature/], {
      allowedTags: ['img', 'input'],
    }) ||
    findFieldKey(fields, [/signature/], {
      allowedTags: ['img', 'input'],
      excludePatterns: [/witness/, /physician/, /admin/],
    })

  const physicianSignatureFieldName =
    findFieldKey(fields, [/physician/, /signature/], {
      allowedTags: ['img', 'input'],
    }) ||
    findFieldKey(fields, [/admin/, /signature/], {
      allowedTags: ['img', 'input'],
    }) ||
    findFieldKey(fields, [/adminsignature/], {
      allowedTags: ['img', 'input'],
    }) ||
    findFieldKey(fields, [/doctor/, /signature/], {
      allowedTags: ['img', 'input'],
    })

  const witnessNameFieldName =
    findFieldKey(fields, [/witness/, /name/], {
      allowedTags: ['input', 'textarea', 'select'],
      excludePatterns: [/signature/],
      excludedTypes: ['hidden'],
    }) ||
    findFieldKey(fields, [/witnessname/], {
      allowedTags: ['input', 'textarea', 'select'],
      excludedTypes: ['hidden'],
    })

  const witnessSignatureFieldName =
    findFieldKey(fields, [/witness/, /signature/], {
      allowedTags: ['img', 'input'],
    }) ||
    findFieldKey(fields, [/witnesssignature/], {
      allowedTags: ['img', 'input'],
    })

  return {
    patientSignatureFieldName,
    physicianSignatureFieldName,
    witnessNameFieldName,
    witnessSignatureFieldName,
  }
}

export const getRequiredFieldValues = (
  values: Record<string, any>,
  fieldNames: RequiredFieldNames,
) => {
  const getValue = (name: string | null) => {
    if (!name) return ''
    const v = getNestedValue(values, name)
    return (v == null ? '' : String(v)).trim()
  }

  return {
    patientSignature: getValue(fieldNames.patientSignatureFieldName),
    physicianSignature: getValue(fieldNames.physicianSignatureFieldName),
    witnessName: getValue(fieldNames.witnessNameFieldName),
    witnessSignature: getValue(fieldNames.witnessSignatureFieldName),
  }
}

const isUnchangedForm = (
  values: Record<string, any>,
  initialValues: Record<string, any>,
): boolean => {
  const currentFlatValues = flattenValues(values)
  const initialFlatValues = flattenValues(initialValues)
  const keys = new Set([...Object.keys(currentFlatValues), ...Object.keys(initialFlatValues)])

  for (const key of keys) {
    const currentValue = (currentFlatValues[key] || '').trim()
    const initialValue = (initialFlatValues[key] || '').trim()
    if (currentValue !== initialValue) {
      return false
    }
  }

  return true
}

export const getTemplateValidationError = (
  values: Record<string, any>,
  fieldNames: RequiredFieldNames,
  initialValues: Record<string, any> = {},
): string | null => {
  const { patientSignature, physicianSignature, witnessName, witnessSignature } =
    getRequiredFieldValues(values, fieldNames)
  const hasPatientSignatureField = Boolean(fieldNames.patientSignatureFieldName)
  const hasPhysicianSignatureField = Boolean(fieldNames.physicianSignatureFieldName)
  const hasWitnessNameField = Boolean(fieldNames.witnessNameFieldName)
  const hasWitnessSignatureField = Boolean(fieldNames.witnessSignatureFieldName)
  const hasAnyRequiredField =
    hasPatientSignatureField ||
    hasPhysicianSignatureField ||
    hasWitnessNameField ||
    hasWitnessSignatureField

  const hasSignatureChangedFromInitial = (
    fieldName: string | null,
    currentValue: string,
  ): boolean => {
    if (!fieldName) return true
    const raw = getNestedValue(initialValues, fieldName)
    const initialValue = (raw == null ? '' : String(raw)).trim()
    return currentValue !== initialValue
  }

  const hasValidPatientSignature =
    !hasPatientSignatureField ||
    (Boolean(patientSignature) &&
      hasSignatureChangedFromInitial(fieldNames.patientSignatureFieldName, patientSignature))
  const hasValidPhysicianSignature =
    !hasPhysicianSignatureField ||
    (Boolean(physicianSignature) &&
      hasSignatureChangedFromInitial(fieldNames.physicianSignatureFieldName, physicianSignature))
  const hasValidWitnessSignature =
    !hasWitnessSignatureField ||
    (Boolean(witnessSignature) &&
      hasSignatureChangedFromInitial(fieldNames.witnessSignatureFieldName, witnessSignature))

  if (!hasAnyRequiredField) {
    return null
  }

  if (isUnchangedForm(values, initialValues)) {
    return 'Form is blank. Press Cancel to exit'
  }

  if (hasPatientSignatureField && !hasValidPatientSignature) {
    return 'Patient Signature is missing'
  }

  if (hasPhysicianSignatureField && !hasValidPhysicianSignature) {
    return 'Physician Signature is missing'
  }

  if (hasWitnessSignatureField && !hasValidWitnessSignature) {
    return 'Witness Signature is missing'
  }

  if (hasWitnessNameField && !witnessName) {
    return 'Witness Name is missing'
  }

  return null
}

export const injectFormValues = (html: string, values: Record<string, any>): string => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')
  const flat = flattenValues(values)

  Object.entries(flat).forEach(([name, value]) => {
    const element = findElementByFieldKey(doc, name) as FormFieldElement | null
    if (!element) return

    if (element instanceof HTMLImageElement) {
      element.src = value
    } else if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') {
        element.checked = value === 'on'

        if (value === 'on') {
          element.setAttribute('checked', '')
        } else {
          element.removeAttribute('checked')
        }
      } else {
        element.value = value
        element.setAttribute('value', value)
      }
    } else if (element instanceof HTMLTextAreaElement) {
      element.value = value
      element.textContent = value
    } else if (element instanceof HTMLSelectElement) {
      element.value = value
    }
  })

  return doc.body.innerHTML
}
